import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { EinzugsgebietMap } from "@/components/calv/EinzugsgebietMap";

export const Route = createFileRoute("/en/about")({
  head: () => {
    const h = pageHead("about", "en");
    return { meta: h.meta, links: h.links };
  },
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Who we are"
          headline={<>We're operators. <em>Not salespeople.</em></>}
          lead="Calvarius is a digital agency. Since 2013 we've supported mid-market companies and selected enterprises — nationally and internationally — in everything that makes online performance. We work like an internal operations team, not an external vendor."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "About" }]}
        />

        <Section
          number="02"
          kicker="Founder"
          headline={<>André Freier — <em>operational, not representational.</em></>}
          caption="Calvarius is run by the managing director. That's not a phrase: André sits on every account himself, leads the audit reviews and owns both strategy and day-to-day."
        >
          <RevealOnScroll>
            <div className="mx-auto max-w-[680px] space-y-7 text-lg leading-[1.65] text-ink-2">
              <p>
                I founded Calvarius in 2013 — with a simple observation:
                online marketing only works when it's thought of economically.
                What started small became a method: thinking about online
                performance as a system, not as a stack of channels.
              </p>
              <p>
                Today I personally run every client project. Not because that scales
                particularly well — but because it's the only way for strategy
                and day-to-day not to drift apart. Most performance problems
                start exactly where the person who pitched the account never
                touches it again.
              </p>
              <p>
                My background is e-commerce, which is why roughly four out of
                five of our client projects come from online retail. Media budgets
                between €10,000 and €200,000 a month, catalogs from a few
                hundred to several thousand SKUs — national and international.
                What holds it all together: the same data model for SEO, SEA,
                CRO and tracking — and the same promise that the managing
                director sits on the account.
              </p>
              <p>
                Calvarius works from Karlstadt am Main, in the heart of Mainfranken. The location has two advantages that are economically relevant to our mandate mix: first, Würzburg, Schweinfurt, and Aschaffenburg are within 25 to 50 minutes — enabling in-person meetings with regional clients without logistical overhead. Second, Frankfurt and Nürnberg are reachable in under two hours — opening the entire Rhine-Main region plus Middle Franconia as a service area. For nationwide mandates, which make up much of our mix, operational management runs digitally anyway — the location here is anchor, not limitation.
              </p>
            </div>
          </RevealOnScroll>

          <Callout>
            If the person who sold the client project never sees the account again,
            something is fundamentally wrong.
          </Callout>
        </Section>

        <LogoReferences locale="en" number="03" />

        <Section
          number="04"
          variant="tinted"
          kicker="How we work"
          headline={<>Three principles that <em>carry every client project.</em></>}
          caption="We say them out loud because we want clients to read them as a contract with us — not as a marketing promise."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {[
                {
                  n: "01",
                  t: "Data first",
                  b: "No client project starts without an audit. If the data isn't sound, every strategy is guesswork. We'd rather invest three weeks in solid tracking than three months in half-informed optimization.",
                },
                {
                  n: "02",
                  t: "Run by the MD",
                  b: "No hand-off to juniors after the pitch. Strategy and execution run through the same person — otherwise they decouple within weeks.",
                },
                {
                  n: "03",
                  t: "Fees without incentive conflict",
                  b: "We don't earn more when you spend more on media. Flat or performance-based — but never as a percentage of ad spend.",
                },
              ].map((p) => (
                <div key={p.n} className="bg-white p-7">
                  <div className="font-mono text-xs text-ink-4">{p.n}</div>
                  <div className="mt-3 font-display text-xl text-ink">{p.t}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          kicker="When we say no"
          headline={<>Some client projects are <em>wrong for us.</em></>}
          caption="Honesty before politeness — even in the first conversation. Four constellations where we actively decline rather than sign a contract."
        >
          <RevealOnScroll>
            <div className="rounded-lg border-l-4 border-orange-500 bg-bg-subtle p-8 md:p-10">
              <ul className="space-y-5 text-base leading-relaxed text-ink-2">
                {[
                  "When the expectation is that an agency replaces — not accelerates — the business model.",
                  "When the tracking setup is broken to the point that no optimization can be measured validly, and the client doesn't want to fix it.",
                  "When the media budget is so small that serious management costs more than it can return.",
                  "When someone wants a percentage of ad spend as fee. We don't do that — the incentive conflict is too big.",
                ].map((line) => (
                  <li key={line} className="flex gap-4">
                    <span className="mt-3 inline-block h-px w-5 shrink-0 bg-orange-500" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="06"
          variant="tinted"
          kicker="Location"
          headline={<>Working <em>nationally and internationally.</em></>}
          caption="We work remote where it makes sense — and on site when it carries the work."
        >
          <RevealOnScroll>
            <div className="grid gap-10 rounded-lg border border-rule bg-white p-8 md:grid-cols-[1.4fr_1fr] md:p-12">
              <div>
                <h3 className="font-display text-2xl text-ink">
                  Quiet work — accountable delivery.
                </h3>
                <p className="mt-4 leading-relaxed text-ink-3">
                  Our work is focused, senior-led and close to the accounts. What clients feel: deeper work,
                  less staff turnover, more senior hours per euro.
                </p>
                <p className="mt-4 leading-relaxed text-ink-3">
                  National
                  and international clients reach us without friction — and we
                  travel to them when it matters.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                  <span>since 2013</span>
                  <span>·</span>
                  <span>national & international</span>
                </div>
              </div>
              <div className="flex items-center justify-center rounded-md bg-cyan-tint p-10">
                <div className="text-center">
                  <div className="mx-auto h-3 w-3 rounded-full bg-cyan-deep ring-4 ring-cyan/30 live-pulse" />
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
National & international
                  </div>
                </div>
              </div>
            </div>
            <EinzugsgebietMap locale="en" />
          </RevealOnScroll>
        </Section>

        <Section
          number="07"
          kicker="Let's talk"
          headline={<>Let's talk about your <em>performance.</em></>}
          caption="If you want to know where your online marketing is currently leaving potential on the table – and how to change that in a structured way, we're happy to talk."
        >
          <CTABlock
            headline={<>On equal footing — without sales pressure.</>}
            body="Tell us briefly what it's about. We'll get back to you within two business days with a proposal for a first conversation, or an honest note if we're not the right fit."
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "See services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
